import { useState, useCallback } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { alphaVantageService } from '@/services/alphaVantage/alphaVantageService';
import { AggregatedSymbol } from '../../types';
import { generateMockMarketData, normalizeSymbol, generateMockCorrelation } from './utils';
import { engineerFeatures, OHLC } from '@/factories/featurePipeline';
import { MarketDataMap } from './types';
import * as alpaca from '@/services/alpaca';

// Helper function to calculate volatility from a price series
function calculateVolatility(prices: number[]): number {
  if (prices.length < 2) return 0.02; // Default value if not enough data
  
  // Calculate daily returns
  const returns: number[] = [];
  for (let i = 1; i < prices.length; i++) {
    returns.push((prices[i] / prices[i-1]) - 1);
  }
  
  // Calculate standard deviation of returns
  const meanReturn = returns.reduce((sum, ret) => sum + ret, 0) / returns.length;
  const variance = returns.reduce((sum, ret) => sum + Math.pow(ret - meanReturn, 2), 0) / returns.length;
  
  return Math.sqrt(variance);
}

export const useMarketDataFetch = (
  apiCallCount: number, 
  setApiCallCount: (cb: (prev: number) => number) => void,
  isDevMode: boolean
) => {
  const { toast } = useToast();
  const [marketData, setMarketData] = useState<MarketDataMap>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch market data for a list of symbols
  const fetchMarketData = useCallback(async (symbols: AggregatedSymbol[]): Promise<Record<string, any>> => {
    if (symbols.length === 0) return {};
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Generate market data with fallbacks for API errors
      const mockMarketData: Record<string, any> = {};
      
      for (const symbol of symbols) {
        // Debug: Log the symbol object structure
        console.log('Processing symbol object:', { 
          fullObject: symbol, 
          symbolField: symbol.symbol, 
          symbolType: typeof symbol.symbol,
          symbolLength: symbol.symbol?.length || 0 
        });
        
        // Skip symbols with empty or invalid symbol fields
        if (!symbol.symbol || symbol.symbol.trim().length === 0) {
          console.warn('Skipping invalid symbol:', symbol);
          continue;
        }
        
        const symbolUpperCase = normalizeSymbol(symbol.symbol);
        console.log(`Processing symbol: ${symbol.symbol} -> ${symbolUpperCase}`);
        
        try {
          // First attempt: use Alpaca to get real data
          // Always try Alpaca first regardless of dev mode since it's working
          if (apiCallCount < 10) {
            // Use environment variables for API credentials - NEVER hardcode them!
            const alpacaKeyId = process.env.VITE_ALPACA_KEY_ID;
            const alpacaSecretKey = process.env.VITE_ALPACA_SECRET_KEY;
            
            if (!alpacaKeyId || !alpacaSecretKey) {
              console.warn('Alpaca API credentials not configured. Using mock data instead.');
              // Skip to mock data generation
              console.warn(`WARNING: API credentials not available for ${symbolUpperCase}, using mock data with fake prices!`);
              mockMarketData[symbolUpperCase] = generateMockMarketData(symbolUpperCase);
              continue;
            }
            
            // Set paper mode from environment (default to true for safety)
            const paperMode = process.env.VITE_ALPACA_PAPER_MODE !== 'false';
            alpaca.setPaperMode(paperMode);
            
            // Initialize with environment variables
            alpaca.config.initialize({
              keyId: alpacaKeyId,
              secretKey: alpacaSecretKey,
              paper: paperMode
            });
            
            try {
              // Atomic increment: get the new count first, then check
              let currentCount = 0;
              setApiCallCount(prev => {
                currentCount = prev + 1;
                return currentCount;
              });
              
              // Check count after increment to avoid race conditions
              if (currentCount > 10) {
                console.warn(`API call limit reached (${currentCount}), skipping Alpaca for ${symbolUpperCase}`);
                throw new Error('API call limit exceeded');
              }
              
              console.log(`Attempting to fetch ${symbolUpperCase} data from Alpaca using ${paperMode ? 'paper' : 'live'} trading...`);
              
              // Get latest quote from Alpaca
              try {
                const quoteData = await alpaca.marketData.getLatestQuote(symbolUpperCase);
                // Only log detailed data in development
                if (isDevMode) {
                  console.log(`Raw Alpaca quote data for ${symbolUpperCase}:`, quoteData);
                }
              
              // Calculate Z-score from historical data (we'll use a simple approach)
              let zscore = 0;
              let volatility = 0.02; // Default value
              
              // For free accounts, we can't access historical bars
              // Instead, we'll generate synthetic historical data based on current price
              try {
                // Skip trying to get historical bars since we know it will fail with 403
                // Free accounts don't have access to historical data
                
                // Create synthetic data based on current price
                const syntheticBars = [];
                
                // Generate 60 days of synthetic data with some random variation
                const endDate = new Date();
                const startDate = new Date();
                startDate.setMonth(startDate.getMonth() - 3); // 3 months
                
                // Mock data for testing
                const barsData = {
                  [symbolUpperCase]: [] 
                };
                
                // For free accounts, we'll just use default values
                // or generate synthetic data based on current price
                
                // Get current price from quote data
                const currentPrice = quoteData.price;
                
                // Generate 20 days of synthetic pricing based on current price
                const syntheticPrices = [];
                
                for (let i = 0; i < 20; i++) {
                  // Generate prices with small random variations (±5% of current price)
                  const randomFactor = 0.95 + Math.random() * 0.1; // 0.95 to 1.05
                  syntheticPrices.push(currentPrice * randomFactor);
                }
                
                // Calculate volatility from synthetic prices
                volatility = calculateVolatility(syntheticPrices);
                
                // Calculate z-score with synthetic data
                const avg = syntheticPrices.reduce((sum, price) => sum + price, 0) / syntheticPrices.length;
                const stdDev = Math.sqrt(syntheticPrices.reduce((sum, price) => sum + Math.pow(price - avg, 2), 0) / syntheticPrices.length);
                zscore = (quoteData.price - avg) / (stdDev || 1); // Avoid division by zero
              } catch (histErr) {
                if (isDevMode) {
                  console.log(`Falling back to mock z-score for ${symbolUpperCase}:`, histErr);
                }
              }
              
              // Properly format the price for display
              const formattedPrice = typeof quoteData.price === 'number' 
                ? quoteData.price 
                : parseFloat(quoteData.price) || 0;
              
              mockMarketData[symbolUpperCase] = {
                lastPrice: formattedPrice,
                zscore,
                volatility,
                correlations: {}
              };
              
              if (isDevMode) {
                console.log(`Successfully loaded Alpaca data for ${symbolUpperCase}: $${formattedPrice}`);
              }
              continue; // Skip to the next symbol
              } catch (timestampError) {
                if (isDevMode) {
                  console.error(`Error processing Alpaca data for ${symbolUpperCase}:`, timestampError);
                }
                console.info(`INFO: Alpaca quote fetch failed, falling back to AlphaVantage for ${symbolUpperCase}`);
                // Continue with AlphaVantage fallback
              }
            } catch (alpacaErr) {
              console.info(`INFO: Alpaca data fetch failed for ${symbolUpperCase}:`, alpacaErr);
              console.info(`INFO: Trying AlphaVantage as fallback for ${symbolUpperCase}...`);
            }
            
            // Second attempt: try AlphaVantage as fallback
            try {
              const quoteData = await alphaVantageService.getQuote(symbolUpperCase);
              
              // Check if we got valid data back
              if (quoteData && quoteData['Global Quote'] && quoteData['Global Quote']['05. price']) {
                const price = parseFloat(quoteData['Global Quote']['05. price']);
                const zscore = await alphaVantageService.calculateZScore(symbolUpperCase);
                const volatility = await alphaVantageService.getVolatility(symbolUpperCase);
                
                mockMarketData[symbolUpperCase] = {
                  lastPrice: price,
                  zscore,
                  volatility,
                  correlations: {}
                };
                
                if (isDevMode) {
                  console.log(`Successfully loaded AlphaVantage data for ${symbolUpperCase}: $${price}`);
                }
                continue; // Skip to the next symbol
              } else {
                throw new Error('Invalid data returned from AlphaVantage');
              }
            } catch (avErr) {
              console.error(`AlphaVantage data fetch failed for ${symbolUpperCase}:`, avErr);
              if (isDevMode) {
                console.log(`Falling back to mock data for ${symbolUpperCase}`);
              }
            }
          }
          
          // Fallback: use mock data in dev mode or if API calls failed
          console.warn(`WARNING: All API sources failed for ${symbolUpperCase}, using mock data with fake prices!`);
          mockMarketData[symbolUpperCase] = generateMockMarketData(symbolUpperCase);
          
          // Feature engineering using synthetic data since we don't have access to Alpaca historical data on free plan
          try {
            console.log(`Generating synthetic historical data for ${symbolUpperCase} for feature engineering`);
            
            // Get current price from existing market data if available, or use a default
            const currentPrice = mockMarketData[symbolUpperCase]?.lastPrice || 100;
            
            // Generate synthetic OHLC data for 250 days (approximately 1 year of trading days)
            const series: OHLC[] = [];
            const today = new Date();
            
            // Keep a running price to simulate price trends
            let runningPrice = currentPrice;
            
            for (let i = 250; i > 0; i--) {
              // Add a slight trend component and random noise
              const trend = (Math.random() - 0.5) * 0.02; // -1% to +1% daily trend
              runningPrice = runningPrice * (1 + trend);
              
              // Add random daily variation
              const dailyVolatility = 0.01; // 1% daily volatility
              const open = runningPrice * (1 + (Math.random() - 0.5) * dailyVolatility);
              const close = runningPrice * (1 + (Math.random() - 0.5) * dailyVolatility);
              const high = Math.max(open, close) * (1 + Math.random() * dailyVolatility);
              const low = Math.min(open, close) * (1 - Math.random() * dailyVolatility);
              const volume = Math.floor(100000 + Math.random() * 10000000); // Random volume
              
              series.push({
                open,
                high,
                low,
                close,
                volume
              });
            }
            
            // Generate technical features from the synthetic OHLC data
            const features = await engineerFeatures(series);
            mockMarketData[symbolUpperCase] = {
              ...mockMarketData[symbolUpperCase],
              features
            };
          } catch (featureErr) {
            console.error(`Error generating features for ${symbolUpperCase}:`, featureErr);
            // Continue without features - market data is still usable
          }
        } catch (err) {
          console.error(`Error fetching data for ${symbolUpperCase}:`, err);
          
          // Fallback to mock data on error
          mockMarketData[symbolUpperCase] = generateMockMarketData(symbolUpperCase);
        }
      }
      
      // Generate correlations between symbols
      const symbolNames = Object.keys(mockMarketData);
      for (let i = 0; i < symbolNames.length; i++) {
        for (let j = i + 1; j < symbolNames.length; j++) {
          const correlation = generateMockCorrelation();
          mockMarketData[symbolNames[i]].correlations[symbolNames[j]] = correlation;
          mockMarketData[symbolNames[j]].correlations[symbolNames[i]] = correlation;
        }
      }
      
      setMarketData(mockMarketData);
      
      toast({
        title: "Market Data Loaded",
        description: `Successfully loaded data for ${symbolNames.length} symbols.`,
        variant: "success",
      });
      
      // Return the fetched data
      return mockMarketData;
    } catch (err: any) {
      setError(err.message || 'Error fetching market data');
      
      toast({
        title: "Data Loading Error",
        description: err.message || 'Failed to load market data',
        variant: "destructive"
      });
      
      // Return empty object on error
      return {};
    } finally {
      setIsLoading(false);
    }
  }, [apiCallCount, isDevMode, toast, setApiCallCount]);

  return {
    marketData,
    setMarketData,
    fetchMarketData,
    isLoading,
    error
  };
};
